/**
 * 날짜 계산 유틸리티 (V60.2 → V165)
 *
 * **목적**: UTC 밀림 재발 방지 및 날짜 계산 로직 통일
 *
 * **원칙**:
 * 1. YYYY-MM-DD 문자열은 반드시 수동 파싱 (new Date() 금지)
 * 2. 모든 날짜 계산은 이 유틸만 사용
 * 3. toISOString() 사용 금지
 *
 * **배경**:
 * - 과거 toISOString() UTC 밀림 이슈 경험
 * - new Date("YYYY-MM-DD")는 브라우저별 차이 존재 (UTC vs 로컬)
 * - 수동 파싱으로 로컬 타임존 보장
 *
 * **V165 추가:**
 * - 회사별 타임존 지원 (KR: Asia/Seoul, CN: Asia/Shanghai)
 * - formatTodayByCompanyCode: 회사 타임존 기준 현재 날짜
 * - formatMonthByCompanyCode: 회사 타임존 기준 현재 월
 */

/**
 * YYYY-MM-DD 문자열을 Date 객체로 변환 (로컬 타임존)
 *
 * **중요**: new Date("YYYY-MM-DD") 대신 이 함수를 사용하세요.
 *
 * @param dateStr YYYY-MM-DD 형식의 날짜 문자열
 * @returns Date 객체 (로컬 타임존)
 *
 * @example
 * parseDate("2025-01-01") // → 2025-01-01 00:00:00 (로컬 타임존)
 * new Date("2025-01-01")  // → 브라우저별 UTC/로컬 차이 발생 ❌
 */
export function parseDate(dateStr: string): Date {
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(year, month - 1, day); // month는 0-based
}

/**
 * 두 날짜 사이의 년 단위 차이 계산
 *
 * **계산 방식**:
 * - 일수 차이를 365.25로 나눔 (윤년 고려)
 * - 소수점 1자리로 반올림
 *
 * @param startDate 시작일 (YYYY-MM-DD)
 * @param endDate 종료일 (YYYY-MM-DD)
 * @returns 년 단위 차이 (소수점 1자리)
 *
 * @example
 * calculateYearsBetween("2020-01-01", "2025-01-01") // → 5.0
 * calculateYearsBetween("2020-01-01", "2025-07-01") // → 5.5
 */
export function calculateYearsBetween(startDate: string, endDate: string): number {
  const start = parseDate(startDate);
  const end = parseDate(endDate);

  const diffMs = end.getTime() - start.getTime();
  const diffDays = diffMs / (1000 * 60 * 60 * 24);
  const diffYears = diffDays / 365.25; // 윤년 고려

  return Math.round(diffYears * 10) / 10; // 소수점 1자리
}

/**
 * 날짜에서 년을 빼는 함수 (UTC 밀림 방지)
 *
 * **사용처**: 진급 후보 산출 시 직위 발령일 역산
 *
 * @param dateStr 기준일 (YYYY-MM-DD)
 * @param years 뺄 년수 (소수점 가능)
 * @returns 계산된 날짜 (YYYY-MM-DD)
 *
 * @example
 * subtractYears("2025-01-01", 3.0) // → "2022-01-01"
 * subtractYears("2025-01-01", 2.5) // → "2022-07-02" (약 2.5년 전)
 */
export function subtractYears(dateStr: string, years: number): string {
  const [year, month, day] = dateStr.split('-').map(Number);

  // 정수 년수와 소수점 부분 분리
  const wholeYears = Math.floor(years);
  const fractionalYears = years - wholeYears;

  // 정수 년수 빼기
  let resultYear = year - wholeYears;

  // 소수점 부분을 일수로 변환하여 빼기 (365.25일 기준)
  const daysToSubtract = Math.round(fractionalYears * 365.25);
  const resultDate = new Date(resultYear, month - 1, day);
  resultDate.setDate(resultDate.getDate() - daysToSubtract);

  // YYYY-MM-DD 형식으로 변환
  const finalYear = resultDate.getFullYear();
  const finalMonth = String(resultDate.getMonth() + 1).padStart(2, '0');
  const finalDay = String(resultDate.getDate()).padStart(2, '0');

  return `${finalYear}-${finalMonth}-${finalDay}`;
}

/**
 * 소수점 1자리로 반올림
 *
 * @param value 숫자
 * @returns 소수점 1자리로 반올림된 숫자
 *
 * @example
 * roundToOneDecimal(3.14159) // → 3.1
 * roundToOneDecimal(2.95)    // → 3.0
 */
export function roundToOneDecimal(value: number): number {
  return Math.round(value * 10) / 10;
}

/**
 * V73: 오늘 날짜를 YYYY-MM-DD 형식으로 반환 (로컬 타임존)
 *
 * **중요**: UTC 밀림 방지를 위해 로컬 타임존 기준으로 포맷팅합니다.
 *
 * @returns 오늘 날짜 (YYYY-MM-DD)
 *
 * @example
 * formatToday() // → "2025-01-15" (오늘이 2025년 1월 15일인 경우)
 */
export function formatToday(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
}

// ============================================================================
// V165: 회사별 타임존 지원
// ============================================================================

/**
 * V165: 회사 코드 → 타임존 매핑 (SSoT)
 *
 * 백엔드의 COMPANY_TIMEZONE_MAP과 동기화 필요
 */
export const COMPANY_TIMEZONE_MAP: Record<string, string> = {
  KR: 'Asia/Seoul',    // UTC+9 (DST 없음)
  CN: 'Asia/Shanghai', // UTC+8 (DST 없음)
};

/**
 * V165: 기본 타임존 (companyCode가 없거나 매핑되지 않은 경우)
 */
export const DEFAULT_TIMEZONE = 'Asia/Seoul';

/**
 * V165: 회사 코드로 타임존 문자열 반환
 *
 * @param companyCode - 회사 코드 (KR, CN 등)
 * @returns IANA 타임존 문자열
 */
export function getTimezoneByCompanyCode(companyCode: string | null | undefined): string {
  if (!companyCode) return DEFAULT_TIMEZONE;
  return COMPANY_TIMEZONE_MAP[companyCode] || DEFAULT_TIMEZONE;
}

/**
 * V165: 특정 타임존 기준으로 Date를 YYYY-MM-DD 문자열로 변환
 *
 * @param date - Date 객체
 * @param timeZone - IANA 타임존 문자열
 * @returns YYYY-MM-DD 형식 문자열
 */
export function toDateStringInTZ(date: Date, timeZone: string): string {
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  return formatter.format(date);
}

/**
 * V165: 특정 타임존 기준으로 Date를 YYYY-MM 문자열로 변환
 *
 * @param date - Date 객체
 * @param timeZone - IANA 타임존 문자열
 * @returns YYYY-MM 형식 문자열
 */
export function toMonthStringInTZ(date: Date, timeZone: string): string {
  const dateStr = toDateStringInTZ(date, timeZone);
  return dateStr.substring(0, 7);
}

/**
 * V165: 회사 코드 기준 현재 날짜 (YYYY-MM-DD)
 *
 * @param companyCode - 회사 코드 (KR, CN 등)
 * @returns YYYY-MM-DD 형식 문자열
 *
 * @example
 * // 현재 시각이 2026-01-01 00:30 KST일 때
 * formatTodayByCompanyCode('KR')  // '2026-01-01'
 * formatTodayByCompanyCode('CN')  // '2025-12-31' (UTC+8 기준)
 */
export function formatTodayByCompanyCode(companyCode: string | null | undefined): string {
  const timeZone = getTimezoneByCompanyCode(companyCode);
  return toDateStringInTZ(new Date(), timeZone);
}

/**
 * V165: 회사 코드 기준 현재 월 (YYYY-MM)
 *
 * @param companyCode - 회사 코드 (KR, CN 등)
 * @returns YYYY-MM 형식 문자열
 *
 * @example
 * formatMonthByCompanyCode('KR')  // '2026-01'
 * formatMonthByCompanyCode('CN')  // '2025-12'
 */
export function formatMonthByCompanyCode(companyCode: string | null | undefined): string {
  const timeZone = getTimezoneByCompanyCode(companyCode);
  return toMonthStringInTZ(new Date(), timeZone);
}

// ============================================================================
// V170.1: 타임존 기반 시간 표시 유틸
// ============================================================================

/**
 * V170.1: Firestore Timestamp/Date를 회사 타임존 기준 시간 문자열로 변환
 *
 * @param timestamp - Firestore Timestamp, Date, 또는 _seconds를 가진 객체
 * @param companyCode - 회사 코드 (KR, CN 등)
 * @param options - Intl.DateTimeFormat 옵션
 * @returns 포맷된 시간 문자열 (예: "09:30")
 *
 * @example
 * formatTimeByCompanyCode(attendance.checkInTime, 'CN')  // "08:30" (CST)
 * formatTimeByCompanyCode(attendance.checkInTime, 'KR')  // "09:30" (KST)
 */
export function formatTimeByCompanyCode(
  timestamp: any,
  companyCode: string | null | undefined,
  options?: Intl.DateTimeFormatOptions
): string {
  if (!timestamp) return '-';

  try {
    const date = timestamp.toDate
      ? timestamp.toDate()
      : timestamp._seconds
      ? new Date(timestamp._seconds * 1000)
      : new Date(timestamp);

    const timeZone = getTimezoneByCompanyCode(companyCode);

    const defaultOptions: Intl.DateTimeFormatOptions = {
      timeZone,
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    };

    return date.toLocaleTimeString('ko-KR', { ...defaultOptions, ...options });
  } catch {
    return '-';
  }
}

/**
 * V170.1: Firestore Timestamp/Date를 회사 타임존 기준 날짜+시간 문자열로 변환
 *
 * **시차 정책 (V62-2/V62-3):**
 * companyCode는 actor(행위자) 기준이 아닌 **데이터 소속 회사 기준**으로 전달.
 * - 승인요청 내 모든 timestamp → request.companyCode (요청자 소속)
 * - 공휴일 → holiday.companyCode
 * - 면담 → share.companyCode
 * - 고용관리 로그 → log.companyCode (대상 직원 소속)
 *
 * **왜 actor 기준이 아닌가? (V62-3 정책 의도)**
 * 동일 요청/데이터 내 모든 timestamp가 동일한 시간 기준을 사용해야 일관성이 유지된다.
 * 예: KR HR이 CN 요청을 승인해도, createdAt·approvedAt·rejectedAt 등이 모두
 * CN 시간으로 표시되어야 시간 순서가 혼동되지 않는다.
 * actor 기준으로 하면 같은 요청 내 timestamp마다 시간대가 달라져 혼란 발생.
 *
 * @param timestamp - Firestore Timestamp, Date, 또는 _seconds를 가진 객체
 * @param companyCode - 회사 코드 (KR, CN 등)
 * @param includeSeconds - 초 단위 포함 여부
 * @returns 포맷된 날짜+시간 문자열 (예: "2026. 01. 02. 09:30:15")
 *
 * @example
 * formatDateTimeByCompanyCode(request.createdAt, 'CN', true)  // "2026. 01. 02. 08:30:15" (CST)
 */
export function formatDateTimeByCompanyCode(
  timestamp: any,
  companyCode: string | null | undefined,
  includeSeconds: boolean = false
): string {
  if (!timestamp) return '-';

  try {
    const date = coerceToDate(timestamp);
    if (!date || isNaN(date.getTime())) return '-';

    const timeZone = getTimezoneByCompanyCode(companyCode);

    const options: Intl.DateTimeFormatOptions = {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      ...(includeSeconds && { second: '2-digit' }),
    };

    return date.toLocaleString('ko-KR', options);
  } catch {
    return '-';
  }
}

/**
 * V66 RCA: Firestore Timestamp / `{_seconds, _nanoseconds}` 객체 / `{seconds, nanoseconds}` 객체 /
 * ISO string / Date / number 어떤 형태든 안전하게 Date 로 변환.
 *
 * 이유: Firebase v2 callable 응답에서 Firestore admin Timestamp 는 `{_seconds, _nanoseconds}` 로
 * 직렬화되어 도착하지만, 일부 진입점(예: 클라이언트 SDK direct read 또는 캐시) 에서는 `{seconds, nanoseconds}`
 * 로 들어올 수 있다. `new Date(<plain object>)` 는 Invalid Date 를 반환하여 `toLocaleString` 결과가
 * 문자열 'Invalid Date' 로 화면에 노출되는 사고가 발생함 (V66 전배 RCA).
 */
function coerceToDate(timestamp: any): Date | null {
  if (timestamp == null) return null;
  if (timestamp instanceof Date) return timestamp;
  if (typeof timestamp.toDate === 'function') return timestamp.toDate();
  if (typeof timestamp === 'object') {
    const sec = typeof timestamp._seconds === 'number'
      ? timestamp._seconds
      : (typeof timestamp.seconds === 'number' ? timestamp.seconds : null);
    if (sec !== null) return new Date(sec * 1000);
  }
  // ISO string 또는 number(ms) fallback
  const d = new Date(timestamp);
  return isNaN(d.getTime()) ? null : d;
}

/**
 * V66 RCA: 회사 타임존 기준 날짜(YYYY. MM. DD.) 문자열로 포맷.
 *  - Firestore Timestamp / `{_seconds, _nanoseconds}` / ISO string / Date 모두 안전 처리.
 *  - Invalid 면 '-' 반환 (Invalid Date 문자열 노출 방지).
 *
 * 사용처: 승인요청 목록의 "요청일" 컬럼처럼 시간 단위가 필요 없는 화면.
 */
export function formatDateByCompanyCode(
  timestamp: any,
  companyCode: string | null | undefined
): string {
  if (!timestamp) return '-';
  try {
    const date = coerceToDate(timestamp);
    if (!date || isNaN(date.getTime())) return '-';

    const timeZone = getTimezoneByCompanyCode(companyCode);
    return date.toLocaleDateString('ko-KR', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  } catch {
    return '-';
  }
}

/**
 * V66: 전배 요청의 "전배일"(실제 진행/예정일) 표시 문자열.
 *
 * 정책 (HrTransfersPage / OrgTransferPage 공통):
 *  1) `appliedAt` 존재 → 실제 적용 완료된 전배 → appliedAt 날짜.
 *  2) `transferEffectiveDate` 또는 `requestData.effectiveDate` 존재 → 예약 전배 발효 예정일 (YYYY-MM-DD).
 *  3) 위 모두 없음 → 즉시 전배 신청/승인 대기 → "승인 즉시".
 *
 * 모든 timestamp 변환은 공용 `formatDateByCompanyCode` 사용 (Firestore Timestamp / `{_seconds, _nanoseconds}` /
 * ISO 모두 안전 처리, V66 Invalid Date RCA 정책).
 *
 * @param req approvalRequest (type='transfer') 객체. `appliedAt`/`transferEffectiveDate`/`requestData.effectiveDate`/`companyCode` 사용.
 */
export function formatTransferEffectiveDate(req: {
  appliedAt?: any;
  transferEffectiveDate?: string | null;
  requestData?: { effectiveDate?: string | null } | null;
  companyCode?: string | null;
}): string {
  if (req.appliedAt) {
    return formatDateByCompanyCode(req.appliedAt, req.companyCode);
  }
  const effDate = req.transferEffectiveDate || req.requestData?.effectiveDate;
  if (effDate && /^\d{4}-\d{2}-\d{2}$/.test(effDate)) {
    // YYYY-MM-DD 문자열은 회사 타임존 변환 불필요 — 그대로 한국식 표기
    const [y, m, d] = effDate.split('-');
    return `${y}. ${m}. ${d}.`;
  }
  return '승인 즉시';
}

/**
 * V170.1: 현재 연도를 회사 타임존 기준으로 반환
 *
 * @param companyCode - 회사 코드 (KR, CN 등)
 * @returns 연도 (숫자)
 */
export function getCurrentYearByCompanyCode(companyCode: string | null | undefined): number {
  const dateStr = formatTodayByCompanyCode(companyCode);
  return parseInt(dateStr.split('-')[0], 10);
}

/**
 * V170.1: 현재 월을 회사 타임존 기준으로 반환 (1-12)
 *
 * @param companyCode - 회사 코드 (KR, CN 등)
 * @returns 월 (1-12)
 */
export function getCurrentMonthByCompanyCode(companyCode: string | null | undefined): number {
  const dateStr = formatTodayByCompanyCode(companyCode);
  return parseInt(dateStr.split('-')[1], 10);
}
